import { defineComponent as _defineComponent } from 'vue'
import { PublicKey } from "@solana/web3.js";
import { computed, ref, watch, watchEffect } from "vue";
import FormField from "@/components/form/FormField.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import { rules } from "@/components/form/rules";
import CommonSpinner from "@/components/common/CommonSpinner.vue";
import ErrorCard from "../common/ErrorCard.vue";
import { storage } from "@/composables/useStorage";
import { fogoSession, getExpiresDuration } from "@/composables/useFogoSession";
import {
  ONE_DAY_IN_MS,
  ONE_HOUR_IN_MS,
  ONE_MINUTE_IN_MS,
  ONE_WEEK_IN_MS,
} from "@/composables/useFogoSession";
import store from "@/store";
import { useWallet } from "@/composables/useWallet";
import { Network } from "@fogo/sessions-sdk";
import { SOL_MINT } from "@/types/constants";

// store

export default /*#__PURE__*/_defineComponent({
  __name: 'FogoWallet',
  setup(__props, { expose }) {

// import { useWalletStore } from '@/stores/walletStore';
const walletStore = computed<ReturnType<typeof useWallet> | undefined>(() => {
  if (!store.state.wallet.wallet) return undefined;
  return store.state.wallet.wallet;
});
// const wallet = useWallet();

// computed
const getDefaultLimits = computed<{ [key: string]: string }>(() => {
  const t: { [key: string]: string } = {};
  const defaults: Record<string, string> = storage.get("FOGO_LIMITS", {});
  for (const token of tokens.value) {
    t[token.publicKey.toString()] = defaults[token.publicKey.toString()] || "";
  }
  return t;
});

// data
const tokens = ref<
  {
    publicKey: PublicKey;
    symbol: string;
    logo: string;
  }[]
>([
  {
    publicKey: SOL_MINT,
    symbol: "FOGO",
    logo: "/img/logo/fogo.svg",
  }, // FOGO token
  {
    publicKey: new PublicKey("C1e7Q5xwkD2fh5ForBTkfZbcBaukRe7SMr5TnCqbNfgR"),
    symbol: "Aeon",
    logo: "/img/logo/aeon.png",
  }, // aeon token
]);
const limits = ref<Map<PublicKey, number>>(new Map());
const opened = ref<boolean>(false);
const loading = ref<boolean>(false);
const err = ref<Error>();

// methods
function requestLimits() {
  if (opened.value) return;
  opened.value = true;
}
async function connectFogoWallet({
  models,
  isValid,
}: {
  models: { [key: string]: string };
  isValid: boolean;
}) {
  try {
    if (!isValid) return;
    if (!walletStore.value || !walletStore.value.wallet) {
      throw new Error("Wallet not connected");
    }

    const expires = getExpiresDuration(Number(models.expires));
    const onlyLimits = { ...models };
    delete onlyLimits.expires;

    if (!models.skipStorage) {
      storage.set("FOGO_LIMITS", onlyLimits, expires);
    }

    for (const [publicKey, limit] of Object.entries(onlyLimits)) {
      const n = Number(limit);
      if (isNaN(n) || n === 0) continue;
      limits.value.set(new PublicKey(publicKey), Number(limit));
    }

    loading.value = true;
    err.value = undefined;
    
    const { error, data } = await fogoSession.getOrEstablishSession({
      wallet: {
        // @ts-expect-error
        publicKey: walletStore.value.wallet.publicKey,
        // @ts-expect-error
        signMessage: walletStore.value.wallet.signMessage,
      },
      options: {
        domain: "https://chronokingdoms.com",
        network: Network.Testnet,
        duration: Number(models.expires),
        // @ts-expect-error i think fogo uses bigint wrongly, because we should accept decimals number
        limits: limits.value,
        expires,
      },
    });
    loading.value = false;

    if (error) {
      throw error;
    }

    opened.value = false;

    store.commit("user/set_fogo_session", {
      session: data.session,
      context: data.context,
      wallet: store.state.wallet.wallet,
    });
    store.commit("user/set_needs_fogo_reconnect", false);
    // TODO: we should enable this to load user worlds on fogo login,
    // const worlds = await store.state.user.client.indexWorlds()

    // if(worlds.length > 0) {
    //   store.commit("user/set_world", worlds[0].publicKey)
    //   store.commit("village/set_world", worlds[0].account)
    // }
  } catch (error) {
    console.error("[FogoWallet] Failed to connect:", error);
    err.value = error as Error;
    fogoSession.disconnect();
  } finally {
    loading.value = false;
  }
}
async function checkIfShouldAutoConnect() {
  if (!walletStore.value?.wallet.connected) return;
  if (store.state.user.needsFogoReconnect) return;
  if (opened.value) return;

  if (store.state.user.session && !fogoSession.isExpired()) {
    return;
  }

  const models: Record<string, string> = storage.get("FOGO_LIMITS", {});
  const noLimits = Object.keys(models).length === 0;

  if (noLimits) {
    requestLimits();
    return;
  }

  models.expires = ONE_WEEK_IN_MS.toString();
  models.skipStorage = "true";

  await connectFogoWallet({
    isValid: true,
    models,
  });
}

// watch
watchEffect(() => {
  if (!walletStore.value?.wallet.connected) return;
  if (store.state.user.needsFogoReconnect) return;
  if (opened.value) return;
  
  checkIfShouldAutoConnect();
});

watch(
  () => store.state.user.needsFogoReconnect,
  (needsReconnect) => {
    if (!needsReconnect) return;
    if (!walletStore.value?.wallet.connected) return;
    
    store.commit("user/clear_fogo_session");
    requestLimits();
    // Flag will be cleared in connectFogoWallet after success
  },
  { immediate: true }
);

// expose
expose({
  requestLimits,
  connectFogoWallet,
  checkIfShouldAutoConnect,
  tokens,
  getDefaultLimits,
  opened,
  err,
  loading,
});

return { __sfc: true,walletStore, getDefaultLimits, tokens, limits, opened, loading, err, requestLimits, connectFogoWallet, checkIfShouldAutoConnect, FormField, FormGroup, rules, CommonSpinner, ErrorCard, ONE_DAY_IN_MS, ONE_HOUR_IN_MS, ONE_MINUTE_IN_MS, ONE_WEEK_IN_MS }
}

})